import { HTMLAttributes, forwardRef, useMemo } from 'react';

import { Flex, FlexProps } from '@reckon-web/flex';
import { XIcon } from '@reckon-web/icon';
import { Text } from '@reckon-web/text';
import { useTheme } from '@reckon-web/theme';
import { DisallowStyleProps, getContrastText } from '@reckon-web/utils';

type ValidBoxProps = Pick<FlexProps, 'data' | 'id'>;
export type TagProps = {
  /** The label of the Tag. */
  label: string;
  /** Callback invoked when the removal icon is clicked. */
  onRemove?: () => void;
  /** The variant of the Tag. */
  variant?: number;
} & DisallowStyleProps &
  ValidBoxProps;

export const Tag = forwardRef<HTMLDivElement, TagProps>(
  // NOTE: we unfortunately must spread props because designs assign tooltips to non-interactive elements like this
  ({ data, id, label, onRemove, variant, ...tooltipProps }, forwardedRef) => {
    const { palette, sizing } = useTheme();

    const backgroundColor =
      typeof variant === 'number'
        ? palette.variant[variant]
        : palette.background.shade;

    const color = useMemo(
      () => getContrastText(backgroundColor),
      [backgroundColor]
    );

    return (
      <Flex
        ref={forwardedRef}
        data={data}
        id={id}
        // styles
        alignItems="center"
        inline
        borderRadius="xsmall"
        height={sizing.xsmall} // FIXME: should be resolved by the `Box` component
        minWidth={0} // fix flex overflow bug that prevents text truncation
        paddingX="small"
        css={{ backgroundColor, color }}
        {...tooltipProps}
      >
        <Text
          inline
          color="inherit"
          leading="tight"
          size="small"
          weight="medium"
          overflowStrategy="truncate"
        >
          {label}
        </Text>

        {onRemove && (
          <RemoveButton onClick={onRemove} aria-label={`Remove ${label}`} />
        )}
      </Flex>
    );
  }
);

const RemoveButton = (props: HTMLAttributes<HTMLButtonElement>) => {
  const { spacing } = useTheme();
  return (
    <button
      type="button"
      css={{
        color: 'inherit',
        cursor: 'pointer',
        left: spacing.xsmall,
        opacity: 0.6,
        position: 'relative',

        ':hover, :focus': {
          opacity: 1,
        },
        ':active': {
          opacity: 0.3,
        },
      }}
      {...props}
    >
      <XIcon size="small" />
    </button>
  );
};
